import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/Input'
import Field from '@/components/ui/Field'
import { Label } from '@/components/ui/Label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card'
import { Smartphone, Shield, ArrowLeft } from 'lucide-react'
import useTwoFactorChallenge from '@/services/api/auth/tfa/useTwoFactorChallenge'
import { zodResolver } from '@hookform/resolvers/zod'
import FieldStatusMessage from '@/components/ui/FieldStatusMessage'

const codeSchema = z.object({
  code: z.string().length(6, 'Authentication code must be 6 digits'),
})

const recoverySchema = z.object({
  recovery_code: z.string().min(1, 'Recovery code is required'),
})

type CodeFormData = z.infer<typeof codeSchema>
type RecoveryFormData = z.infer<typeof recoverySchema>

export function TwoFactorChallengePage() {
  const [activeTab, setActiveTab] = useState('code')

  const codeForm = useForm<CodeFormData>({
    mode: 'onChange',
    resolver: zodResolver(codeSchema),
    defaultValues: {
      code: '',
    },
  })

  const recoveryForm = useForm<RecoveryFormData>({
    mode: 'onChange',
    resolver: zodResolver(recoverySchema),
    defaultValues: {
      recovery_code: '',
    },
  })

  const { action: challenge, status } = useTwoFactorChallenge()

  const onCodeSubmit = (data: CodeFormData) => {
    challenge({ code: data.code, recoveryCode: undefined })
  }

  const onRecoverySubmit = (data: RecoveryFormData) => {
    challenge({ recoveryCode: data.recovery_code, code: undefined })
  }

  const isPending = status.status === 'pending'

  return (
    <div className="mx-auto grid min-h-screen w-full max-w-md">
      <Card className="place-self-center">
        <CardHeader className="text-center">
          <div className="mb-4 flex items-center justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
              <Shield className="h-6 w-6 text-primary" />
            </div>
          </div>
          <CardTitle>Two-Factor Authentication</CardTitle>
          <CardDescription>
            Please verify your identity to continue
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="code" className="flex items-center gap-2">
                <Smartphone className="h-4 w-4" />
                App Code
              </TabsTrigger>
              <TabsTrigger value="recovery" className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Recovery Code
              </TabsTrigger>
            </TabsList>

            <TabsContent value="code" className="mt-6">
              <form
                onSubmit={codeForm.handleSubmit(onCodeSubmit)}
                className="space-y-4"
              >
                <Field
                  status={
                    codeForm.formState.errors.code?.message
                      ? 'error'
                      : undefined
                  }
                  statusText={codeForm.formState.errors.code?.message}
                >
                  <Label htmlFor="code">Authentication Code</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="text-center tracking-widest"
                    {...codeForm.register('code', {
                      required: 'Authentication code is required',
                      minLength: {
                        value: 6,
                        message: 'Authentication code must be 6 digits',
                      },
                      maxLength: {
                        value: 6,
                        message: 'Authentication code must be 6 digits',
                      },
                    })}
                  />
                  <FieldStatusMessage />
                  <p className="text-sm text-txt-secondary">
                    Enter the 6-digit code from your authenticator app
                  </p>
                </Field>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={isPending}
                >
                  {isPending ? 'Verifying...' : 'Verify Code'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="recovery" className="mt-6">
              <form
                onSubmit={recoveryForm.handleSubmit(onRecoverySubmit)}
                className="space-y-4"
              >
                <Field
                  status={
                    recoveryForm.formState.errors.recovery_code?.message
                      ? 'error'
                      : undefined
                  }
                  statusText={
                    recoveryForm.formState.errors.recovery_code?.message
                  }
                >
                  <Label htmlFor="recovery_code">Recovery Code</Label>
                  <Input
                    id="recovery_code"
                    type="text"
                    placeholder="Enter recovery code"
                    className=""
                    {...recoveryForm.register('recovery_code', {
                      required: 'Recovery code is required',
                    })}
                  />
                  <FieldStatusMessage />
                  <p className="text-sm text-txt-secondary">
                    Enter one of your recovery codes
                  </p>
                </Field>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={isPending}
                >
                  {isPending ? 'Verifying...' : 'Use Recovery Code'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          {/* <div className="mt-6 border-t pt-6">
            <Button
              variant="ghost"
              className="flex w-full items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Login
            </Button>
          </div> */}
        </CardContent>
      </Card>
    </div>
  )
}
